/**
 * Section heures supplémentaires pour l'onglet Équipe
 * @module components/heures-sup/HeuresSupSection
 */

import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Clock, Plus, TrendingUp } from 'lucide-react';

import HeuresSupTable from './HeuresSupTable';
import HeuresSupModal from './HeuresSupModal';
import { useHeuresSup } from '@/hooks/useHeuresSup';
import type { HeureSup, HeuresSupSettings, CreateHeureSupData } from '@/types/heures-sup.types';
import { formatHeures, formatCoutHeuresSup } from '@/lib/calculs-heures-sup';

interface HeuresSupSectionProps {
  chantierId: string;
  entrepriseId: string;
  membresAffectes: Array<{
    id: string;
    nom: string;
    prenom: string;
    taux_horaire: number;
  }>;
  settings: HeuresSupSettings;
}

export default function HeuresSupSection({
  chantierId,
  entrepriseId,
  membresAffectes,
  settings,
}: HeuresSupSectionProps) {
  // Si les heures sup ne sont pas activées, ne rien afficher
  if (!settings.heures_sup_actives) {
    return null;
  }

  const [modalOpen, setModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<HeureSup | undefined>(undefined);

  // Hook pour les heures sup
  const {
    heuresSup,
    isLoading,
    totalHeures,
    totalCout,
    ajouterHeureSup,
    modifierHeureSup,
    supprimerHeureSup,
  } = useHeuresSup(chantierId);

  // Ouvrir le modal pour ajouter
  const handleAdd = () => {
    setEditingEntry(undefined);
    setModalOpen(true);
  };

  // Ouvrir le modal pour éditer
  const handleEdit = (entry: HeureSup) => {
    setEditingEntry(entry);
    setModalOpen(true);
  };

  // Supprimer une entrée
  const handleDelete = async (id: string) => {
    await supprimerHeureSup(id);
  };

  // Sauvegarder (ajout ou modification)
  const handleSave = async (data: CreateHeureSupData) => {
    if (editingEntry) {
      await modifierHeureSup(editingEntry.id, {
        date_travail: data.date_travail,
        nombre_heures: data.nombre_heures,
        type_heure: data.type_heure,
        taux_majoration: data.taux_majoration,
        cout_total: data.cout_total,
        notes: data.notes,
      });
    } else {
      await ajouterHeureSup(data);
    }
  };

  return (
    <Card className="mt-6">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-500/10 rounded-lg">
              <Clock className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <CardTitle className="text-lg">Heures supplémentaires</CardTitle>
              <CardDescription>
                Gérez les heures sup de ce chantier
              </CardDescription>
            </div>
          </div>
          <Button onClick={handleAdd} size="sm">
            <Plus className="w-4 h-4 mr-2" />
            Ajouter des heures sup
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Résumé si des heures sup existent */}
        {heuresSup.length > 0 && (
          <div className="flex items-center gap-6 p-4 border rounded-lg bg-muted/30">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Total :</span>
            </div>
            <Badge variant="secondary" className="text-base">
              {formatHeures(totalHeures)}
            </Badge>
            <span className="text-muted-foreground">|</span>
            <Badge variant="outline" className="text-base border-amber-500/50 text-amber-500">
              {formatCoutHeuresSup(totalCout)}
            </Badge>
          </div>
        )}

        {/* Tableau des heures sup */}
        <HeuresSupTable
          heuresSup={heuresSup}
          isLoading={isLoading}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />

        {/* Modal d'ajout/édition */}
        <HeuresSupModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          chantierId={chantierId}
          entrepriseId={entrepriseId}
          membresAffectes={membresAffectes}
          editingEntry={editingEntry}
          settings={settings}
          onSave={handleSave}
        />
      </CardContent>
    </Card>
  );
}
